import { DatePipe } from '@angular/common';
import { Component, OnInit, Pipe, PipeTransform, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, timeout } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { InvoiceCacheService } from '../../services/invoice-cache.service';
import { InvoiceService } from '../../services/invoice.service';
import { SharedService } from '../../services/shared.service';
import { formatOopsErrorMessage, validateDecimalInput } from '../../shared-data/helper-functions';

/** Invoice review status IDs (match backend). */
const REVIEW_STATUS = { PENDING: 1, FLAGGED: 2, APPROVED: 3 } as const;

/** One row in the Invoice and Kroll amount comparison table (matched by Rx # + fill date). */
export interface InvoiceKrollCompareRow {
  matchKey: string;
  fillDate: any;
  origRxNum: number;
  rxNum: number;
  quantity: number;
  drug: string;
  din: string;
  cost: number;
  fee: number;
  total: number;
  subPlanCode: string;
  taxable: boolean;
  invoiceTaxable: boolean | null;
  krollTaxable: boolean | null;
  taxableDiffers: boolean;
  reason: string;
  invoicePays: number | null;
  krollPays: number | null;
  paysDiff: number | null;
  paysMatch: boolean;
  inInvoiceOnly: boolean;
  inKrollOnly: boolean;
  costDiffers: boolean;
  feeDiffers: boolean;
  totalDiffers: boolean;
  krollCost: number | null;
  krollFee: number | null;
  krollTotal: number | null;
}

@Component({
  selector: 'app-invoice-review-details',
  templateUrl: './invoice-review-details.component.html',
  styleUrls: ['./invoice-review-details.component.css'],
})
export class InvoiceReviewDetailsComponent implements OnInit {

  constructor(private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
    private invoiceService: InvoiceService,
    private invoiceCacheService: InvoiceCacheService,
    private datepipe: DatePipe) { }

  @ViewChild('RemoveConfirmModalClosebutton', { static: false }) closebutton;

  validateDecimalInput: Function = validateDecimalInput;

  showErrorMessage: boolean;
  errorMessage: string;
  invoiceKroll: any = {
    invoiceItemDtos: []
  };
  invoice: any = {
    invoiceItemDtos: []
  };
  invoiceItemPlans: any = [];
  invoiceItem: any = {};
  invoiceItemPlanDtos: any = [];
  searchStartDate: any;
  searchEndDate: any;
  showLoadingMessage: boolean;
  residents: any = [];
  patientId: number;
  rxNum: any;
  invoiceItemPays: number;
  invoiceItemTotal: number;
  invoiceItemFee: number;
  invoiceItemCost: number;
  invoiceItemTaxable: boolean;
  newInvoiceItem: any = {};
  hasIaPlan: boolean;
  errorMessageAddModal: string;
  showErrorMessageAddModal: boolean = false;
  invoiceId: number;
  pharmacyId: number;
  facilityId: number;
  startDate: string;

  ngOnInit() {
    this.sharedService.reloadUserPrivileges();
    this.route.params.subscribe(params => {
      this.invoiceId = +params['invoiceId'];
      this.pharmacyId = +params['pharmacyId'];
      this.facilityId = +params['facilityId'];
      this.patientId = +params['patientId'];
      this.startDate = params['startDate'];

      if (isNaN(this.invoiceId)
        || isNaN(this.pharmacyId)
        || isNaN(this.facilityId)
        || isNaN(this.patientId)
        || !this.startDate) {
        this.router.navigate(['invoice/review']);
        return;
      }

      this.loadFields();
    });
  }

  /** Returns Swal config for status change actions. */
  private getStatusChangeConfig(status: number, isForcePending: boolean = false): any {
    const patientName = (this.invoice && this.invoice.billedTo) ? this.invoice.billedTo : 'Unknown';
    const configs = {
      1: { // Pending (Cancel Approval)
        icon: 'warning' as const,
        title: isForcePending ? 'Force Pending (Dangerous)' : 'Cancel Approval',
        html: isForcePending
          ? `<p>You are about to <strong class="text-danger">force</strong> the invoice back to <strong>Pending</strong> status.</p>
             <p>This will allow updating the Sage invoice for <strong>${patientName}</strong>.</p>
             <p class="text-danger mb-0"><strong>⚠ Use with caution:</strong> Only for Sage update workflow.</p>`
          : `<p>Are you sure you want to cancel the approval on <strong>${patientName}</strong> invoice and set it to <strong>Pending</strong>?</p>`,
        confirmButtonText: isForcePending ? 'Yes, force to Pending' : 'Yes, cancel approval',
        confirmButtonColor: isForcePending ? '#b91c1c' : '#d33'
      },
      2: { // Flagged
        icon: 'warning' as const,
        title: 'Set Invoice Status to Flagged',
        html: `<p>Are you sure you want to apply the status <strong>Flagged</strong> on <strong>${patientName}</strong> invoice?</p>`,
        confirmButtonText: 'Yes, flag it',
        confirmButtonColor: '#dc3545'
      },
      3: { // Approved
        icon: 'question' as const,
        title: 'Set Invoice Status to Approved',
        html: `<p>Are you sure you want to apply the status <strong>Approved</strong> on <strong>${patientName}</strong> invoice?</p>`,
        confirmButtonText: 'Yes, approve it',
        confirmButtonColor: '#28a745'
      }
    };
    return configs[status] || configs[1];
  }

  /** True when invoice is Approved; blocks Flag/Approve actions but allows Cancel Approval. */
  get disableStatusButton(): boolean {
    return this.invoice.invoiceReviewStatusId === REVIEW_STATUS.APPROVED;
  }

  /** Privilege.InvoiceCreate — shows Force Pending (same flow as PENDING). */
  get hasInvoiceCreatePrivilege(): boolean {
    const v = this.sharedService.userPrivilegeVo;
    if (v == null) {
      return false;
    }
    return v.hasInvoiceAccessCreate === true || v.HasInvoiceAccessCreate === true;
  }

  /** Settings → User privilege — Invoice and Kroll amount comparison panel. */
  get hasSettingsUserAccess(): boolean {
    const v = this.sharedService.userPrivilegeVo;
    if (v == null) {
      return false;
    }
    return v.hasSettingsUserAccess === true || v.HasSettingsUserAccess === true;
  }

  get sentToSage(): Boolean {
    return this.invoice.invoiceSageStatusId >= 3;
  }

  /** CREATE: enabled when Approved and Sage not yet completed (< 3). */
  get disableCreateSalesInvoiceButton(): boolean {
    return !this.invoice
      || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.invoice.invoiceSageStatusId >= 3;
  }

  /** UPDATE: enabled when Approved and Sage completed (3: Completed, 4: CompletedWithNewUser, 5: Updated). */
  get disableUpdateSalesInvoiceButton(): boolean {
    return !this.invoice
      || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED
      || (this.invoice.invoiceSageStatusId !== 3 && this.invoice.invoiceSageStatusId !== 4 && this.invoice.invoiceSageStatusId !== 5);
  }

  /**
   * Treat invoiceNumber as a string (coerce + trim).
   * CREATE: null/undefined, empty after trim, or exactly "0".
   * UPDATE: any other non-empty string.
   */
  private hasPersistedInvoiceId(): boolean {
    if (!this.invoice || this.invoice.invoiceNumber == null) {
      return false;
    }
    const s = String(this.invoice.invoiceNumber).trim();
    return s.length > 0 && s !== '0';
  }

  /** No invoice number yet → only Create is shown. */
  get showCreateSalesInvoiceButton(): boolean {
    return !!this.invoice && !this.hasPersistedInvoiceId();
  }

  /** Invoice number exists → only Update is shown. */
  get showUpdateSalesInvoiceButton(): boolean {
    return !!this.invoice && this.hasPersistedInvoiceId();
  }

  /**
   * Force Pending: enabled only when invoice is Approved, Sage flow completed (invoiceSageStatusId >= 3),
   * and has a valid invoice number — allows forcing back to Pending for Sage update workflow.
   */
  get disableForcePendingButton(): boolean {
    return !this.invoice
      || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.invoice.invoiceSageStatusId < 3
      || !this.hasPersistedInvoiceId();
  }

  get inoviceStartDate(): string {
    if (!this.invoice || !this.invoice.startDate) return '';
    return this.transformDate(this.invoice.startDate);
  }

  get inoviceEndDate(): string {
    if (!this.invoice || !this.invoice.endDate) return '';
    return this.transformDate(this.invoice.endDate);
  }

  get hasPreviousInvoice(): boolean {
    const invoice = this.invoiceCacheService.getPreviousInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId);
    return invoice === null;
  }

  get hasNextInvoice(): boolean {
    const invoice = this.invoiceCacheService.getNextInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId);
    return invoice === null;
  }

  get invoiceStatus(): string {
    if (!this.invoice || !this.invoice.invoiceReviewStatusId)
      return 'Status <span class="">-</span>';

    switch (this.invoice.invoiceReviewStatusId) {
      case 1:
        return 'Status <span class="text-warning">Pending</span>';
      case 2:
        return 'Status <span class="text-danger">Flagged</span>';
      case 3:
        return 'Status <span class="text-success">Approved</span>';
      default:
        return 'Status <span class="">-</span>';
    }
    
  }

  transformDate(date) {
    var dateCoverted = Date.parse(date);
    return this.datepipe.transform(dateCoverted, 'MMMM dd, yyyy');
  }

  openInvoice() {
    this.router.navigate([]).then(result => { window.open('report/invoice/' + this.invoice.id + '/' + 'Invoice', '_blank'); });
  }

  /** Shows SweetAlert2 confirmation for status change (FLAG/APPROVE/PENDING/FORCE PENDING). */
  showStatusChangeConfirm(status: number, isForcePending: boolean = false): void {
    const config = this.getStatusChangeConfig(status, isForcePending);

    Swal.fire({
      icon: config.icon,
      title: config.title,
      html: config.html,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: config.confirmButtonText,
      confirmButtonColor: config.confirmButtonColor,
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    }).then(result => {
      if (result.isConfirmed) {
        this.saveUpdateInvoice(status);
      }
    });
  }

  async saveUpdateInvoice(status: number = 0): Promise<void> {
    if (this.shouldBlockStatusChange(status)) return;

    if (status > 0) {
      this.invoice.invoiceReviewStatusId = status;
    }

    this.showLoadingMessage = true;
    try {
      const result = await this.invoiceService.saveNewInvoice(this.invoice, this.sharedService.getUserId());
      if (!result.success) {
        this.loadErrorMessage(result.data);
        this.showLoadingMessage = false;
        return Promise.reject();
      }

      if (this.invoice.id === 0) {
        this.applyNewInvoiceResult(result.data);
      }

      this.showLoadingMessage = false;
      return Promise.resolve();
    } catch (error) {
      this.loadErrorMessage(error.data);
      this.showLoadingMessage = false;
      return Promise.reject();
    }
  }

  /** Block Flag/Approve when already Approved; allow Cancel Approval (→ Pending). */
  private shouldBlockStatusChange(status: number): boolean {
    return this.disableStatusButton && status !== REVIEW_STATUS.PENDING;
  }

  private applyNewInvoiceResult(data: any): void {
    this.invoiceCacheService.updateIdCurrentInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId, data);
    this.updateRouteValues(data.id);
    this.invoice.id = data.id;
    if (data.invoiceReviewStatusId === REVIEW_STATUS.APPROVED) {
      this.invoice = data;
    }
    this.invoiceId = data.id;
    this.loadFields();
  }

  async onSendUpdateSage(): Promise<void> {
    if (!this.disableStatusButton) return;

    if (this.invoice.id === 0) {
      this.errorMessage = "Please, save the invoice first before sending it to Sage!";
      this.showErrorMessage = true;
      window.scroll(0, 0);
      return Promise.resolve();
    }

    this.showLoadingMessage = true;
    try {
      var result = await this.invoiceService.sendUpdateSage(this.invoice.id, this.sharedService.getUserId());

      if (!result.success) {
        this.loadErrorMessage(result.data);
        this.showLoadingMessage = false;
        return Promise.resolve();
      }

      this.invoice = result.data;      
    } catch (e) {
      this.loadErrorMessage(e.data);
      this.showLoadingMessage = false;
      return Promise.reject();
    }
    this.showLoadingMessage = false;
    return Promise.resolve();
  }

  updateRouteValues(newInvoiceId: number) {
    const {
      pharmacyId,
      facilityId,
      patientId,
      startDate
    } = this.route.snapshot.params;

    this.router.navigate([
      '/invoice/review-detail',
      newInvoiceId,
      pharmacyId,
      facilityId,
      patientId,
      startDate
    ]);
  }


  loadErrorMessage(message: any) {
    console.error(message);
    this.showErrorMessage = true;
    this.errorMessage = formatOopsErrorMessage(message);
    window.scroll(0, 0);
  }

  clearErrorMessage() {
    this.showErrorMessage = false;
    this.errorMessage = "";
  }

  validateDate(): string[] {
    var errors: string[] = [];
    if (this.getDate(this.searchEndDate) < this.getDate(this.searchStartDate))
      errors.push("The End Date must be greater then Start Date!")

    if (this.patientId == 0)
      errors.push("Please, Select the Resident first.")

    return errors;
  }

  getDate(date): Date {
    date.setHours(0, 0, 0, 0);
    return date;
  }

  reloadNavigation(invoice) {
    this.invoiceId = invoice.id;
    this.patientId = invoice.patientId;
    this.router.navigate([
      'invoice/review-detail',
      invoice.id,
      this.pharmacyId,
      this.facilityId,
      this.patientId,
      this.startDate
    ]);
    this.loadFields();
  }

  onMovePreviousInvoice() {
    const previousInvoice = this.invoiceCacheService.getPreviousInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId);
    if (previousInvoice) {
      this.reloadNavigation(previousInvoice);
    }
  }

  onMoveNextInvoice() {
    const nextInvoice = this.invoiceCacheService.getNextInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId);
    if (nextInvoice) {
      this.reloadNavigation(nextInvoice);
    }
  }

  loadFields() {
    this.invoiceKroll = {
      invoiceItemDtos: []
    };
    this.invoice = {
      invoiceItemDtos: []
    };

    this.showLoadingMessage = true;

    const invoiceList = this.invoiceCacheService.getInvoices();
    
    if (invoiceList.length === 0) {
      this.invoiceService.getNewInvoices(this.pharmacyId, this.facilityId, this.startDate).subscribe(result => {
        if (!result.success) {
          this.loadErrorMessage(result.data);
        }
        this.invoiceCacheService.setInvoices(result.data);
      });
    }
    
    const kroll$ : Observable<any> = this.invoiceService.getNewKrollInvoice(this.pharmacyId, this.facilityId, this.patientId, this.startDate).pipe(
      catchError(err => {
        this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        return of(null); 
      })
    );

    const invoice$ : Observable<any> = this.invoiceService.getNewPatientInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId, this.startDate).pipe(
      catchError(err => {
        this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        return of(null); 
      })
    );

    forkJoin([kroll$, invoice$]).subscribe(([krollResult, invoiceResult]) => {
      if (krollResult) {
        this.invoiceKroll = krollResult.data;
      }

      if (invoiceResult) {
        this.invoice = invoiceResult.data;
      }

      this.rebuildInvoiceKrollCompareRows();
      this.showLoadingMessage = false; 
    });
  } 

  public removeEprescriber() {
    this.invoice.invoiceItemDtos = this.invoice.invoiceItemDtos.filter(i => i.rxNum !== this.rxNum);

    this.closebutton.nativeElement.click();
    this.loadSummary();
    this.rebuildInvoiceKrollCompareRows();
  }

  public openDetailsModal(rxNum) {
    this.loadInvoiceItem(rxNum);
    this.invoiceItemPlanDtos = this.invoiceItem.invoiceItemPlanDtos;
  }

  private loadInvoiceItem(rxNum: any) {
    this.rxNum = rxNum;
    this.invoiceItem = this.invoice.invoiceItemDtos.find(i => i.rxNum == this.rxNum);
  }

  public openRemoveModal(rxNum) {
    this.loadInvoiceItem(rxNum);
  }

  public openEditModal(rxNum) {
    this.loadInvoiceItem(rxNum);    
    this.invoiceItemTotal = this.invoiceItem.total;
    this.invoiceItemFee = this.invoiceItem.fee;
    this.invoiceItemCost = this.invoiceItem.cost;
    this.invoiceItemPays = this.invoiceItem.pays;
    this.invoiceItemTaxable = this.invoiceItem.taxable;
  }

  public saveInvoiceItemPays() {
    this.invoiceItem.total = Number(this.invoiceItemTotal);
    this.invoiceItem.fee = Number(this.invoiceItemFee);
    this.invoiceItem.cost = Number(this.invoiceItemCost);
    this.invoiceItem.pays = Number(this.invoiceItemPays);
    this.invoiceItem.taxable = Boolean(this.invoiceItemTaxable);

    this.invoiceItem.reason = "Edited by " + this.sharedService.userPrivilegeVo.fullName;
    this.invoiceItem.reasonCode = 8;

    this.loadSummary();
    this.rebuildInvoiceKrollCompareRows();
  }


  private loadSummary() {
    this.invoice.subTotal = Number(parseFloat(this.subTotal.toString()).toFixed(2));
    this.invoice.subTotalTaxableItems = Number(parseFloat(this.subTotalTaxableItems.toString()).toFixed(2));
    this.invoice.subTotalNonTaxableItems = Number(parseFloat(this.subTotalNonTaxableItems.toString()).toFixed(2));
    this.invoice.hst = Number(parseFloat(this.hst.toString()).toFixed(2));
    this.invoice.total = Number(parseFloat(this.total.toString()).toFixed(2));
  }

  public openAddInvoiceItemModal() {
    if (this.disableStatusButton) return;
    this.newInvoiceItem = {};
  }

  public saveNewInvoiceItem() {
    if (this.disableStatusButton) return;

    if (this.ValidateFieldsNewInvoice()) {
      this.showErrorMessageAddModal = true;
      this.errorMessageAddModal = "All fields are mandatory!";
    } else {
      this.clearErrorMessageAddModal();
      this.newInvoiceItem.reason = "Edited by " + this.sharedService.userPrivilegeVo.fullName;
      this.newInvoiceItem.reasonCode = 8;
      this.invoice.invoiceItemDtos.push(this.newInvoiceItem);
      this.loadSummary();
      this.rebuildInvoiceKrollCompareRows();
      document.getElementById("closeAddModalButton").click();
    }
  }

  private ValidateFieldsNewInvoice() {
    return this.newInvoiceItem.rxNum == null ||
      this.newInvoiceItem.fillDate == null ||
      this.newInvoiceItem.quantity == null ||
      this.newInvoiceItem.drug == null ||
      this.newInvoiceItem.din == null ||
      this.newInvoiceItem.cost == null ||
      this.newInvoiceItem.fee == null ||
      this.newInvoiceItem.total == null ||
      this.newInvoiceItem.pays == null;
  }

  clearErrorMessageAddModal() {
    this.showErrorMessageAddModal = false;
    this.errorMessageAddModal = "";
  }

  get subTotal(): number {
    if (this.invoice.invoiceItemDtos) {
      return this.invoice.invoiceItemDtos.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
    } else {
      return 0;
    }
  }

  get subTotalTaxableItems(): number {
    if (this.invoice.invoiceItemDtos) {
      const taxableItems = this.invoice.invoiceItemDtos.filter(item => Boolean(item.taxable));
      return taxableItems.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
    } else {
      return 0;
    }
  }

  get subTotalNonTaxableItems(): number {
    if (this.invoice.invoiceItemDtos) {
      const nonTaxableItems = this.invoice.invoiceItemDtos.filter(item => !Boolean(item.taxable));
      return nonTaxableItems.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
    } else {
      return 0;
    }
  }

  get hst(): number {
    if (this.invoice.invoiceItemDtos && !this.hasIaPlan) {
      const taxableItems = this.invoice.invoiceItemDtos.filter(item => Boolean(item.taxable));
      const paysSum = taxableItems.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
      return Math.round(Number(paysSum) * 0.13 * 100) / 100;
    } else {
      return 0;
    }
  }

  get total(): number {
    return this.subTotal + this.hst;
  }

  onAmountBlurEdit() {
    this.invoiceItemTotal = Number(this.invoiceItemCost) + Number(this.invoiceItemFee);
  }

  onAmountBlurAdd() {
    this.newInvoiceItem.total = Number(this.newInvoiceItem.cost) + Number(this.newInvoiceItem.fee);
  }

  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  sortColumnKroll: string = '';
  sortDirectionKroll: 'asc' | 'desc' = 'asc';
  sortColumnCompare: string = 'fillDate';
  sortDirectionCompare: 'asc' | 'desc' = 'asc';
  private _invoiceKrollCompareRows: InvoiceKrollCompareRow[] = [];

  get invoiceKrollCompareRows(): InvoiceKrollCompareRow[] {
    return this._invoiceKrollCompareRows;
  }

  get invoiceKrollComparePaysMismatchCount(): number {
    return this._invoiceKrollCompareRows.filter(r => !r.paysMatch).length;
  }

  get compareTotalInvoicePays(): number {
    let sum = 0;
    for (let i = 0; i < this._invoiceKrollCompareRows.length; i++) {
      const pays = this._invoiceKrollCompareRows[i].invoicePays;
      if (pays != null) {
        sum += Number(pays);
      }
    }
    return Number(sum.toFixed(2));
  }

  get compareTotalKrollPays(): number {
    let sum = 0;
    for (let i = 0; i < this._invoiceKrollCompareRows.length; i++) {
      const pays = this._invoiceKrollCompareRows[i].krollPays;
      if (pays != null) {
        sum += Number(pays);
      }
    }
    return Number(sum.toFixed(2));
  }

  get compareTotalPaysDiff(): number {
    return Number((this.compareTotalInvoicePays - this.compareTotalKrollPays).toFixed(2));
  }

  get compareTotalsPaysMatch(): boolean {
    return this.compareAmountsEqual(this.compareTotalInvoicePays, this.compareTotalKrollPays);
  }

  public sort(column: string, table: string = 'invoice') {
    let data = [];
    let currentSortColumn = '';
    let currentSortDirection = 'asc';

    if (table === 'invoice') {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
      currentSortColumn = this.sortColumn;
      currentSortDirection = this.sortDirection;
      data = this.invoice.invoiceItemDtos;
    } else if (table === 'compare') {
      if (this.sortColumnCompare === column) {
        this.sortDirectionCompare = this.sortDirectionCompare === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumnCompare = column;
        this.sortDirectionCompare = 'asc';
      }
      currentSortColumn = this.sortColumnCompare;
      currentSortDirection = this.sortDirectionCompare;
      data = this._invoiceKrollCompareRows;
    } else {
      if (this.sortColumnKroll === column) {
        this.sortDirectionKroll = this.sortDirectionKroll === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumnKroll = column;
        this.sortDirectionKroll = 'asc';
      }
      currentSortColumn = this.sortColumnKroll;
      currentSortDirection = this.sortDirectionKroll;
      data = this.invoiceKroll.invoiceItemDtos;
    }

    if (!data) return;

    data.sort((a, b) => {
      let valA = a[column];
      let valB = b[column];

      if (table === 'compare' && column === 'taxable') {
        valA = a.taxable ? 1 : 0;
        valB = b.taxable ? 1 : 0;
      }

      // Handle null/undefined
      if (valA == null) valA = '';
      if (valB == null) valB = '';

      let res = 0;
      if (typeof valA === 'number' && typeof valB === 'number') {
        res = valA - valB;
      } else if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        res = (valA === valB) ? 0 : (valA ? 1 : -1);
      } else {
        // Simple string comparison for everything else (including dates as strings)
        res = valA.toString().localeCompare(valB.toString(), undefined, { numeric: true, sensitivity: 'base' });
      }

      return currentSortDirection === 'asc' ? res : -res;
    });

    if (table === 'compare') {
      this._invoiceKrollCompareRows = data;
    }
  }

  private rebuildInvoiceKrollCompareRows(): void {
    if (!this.hasSettingsUserAccess) {
      this._invoiceKrollCompareRows = [];
      return;
    }
    const invoiceItems = (this.invoice && this.invoice.invoiceItemDtos) ? this.invoice.invoiceItemDtos : [];
    const krollItems = (this.invoiceKroll && this.invoiceKroll.invoiceItemDtos) ? this.invoiceKroll.invoiceItemDtos : [];
    const krollByKey: { [key: string]: any[] } = {};

    for (let i = 0; i < krollItems.length; i++) {
      const item = krollItems[i];
      const key = this.getInvoiceKrollMatchKey(item);
      if (!krollByKey[key]) {
        krollByKey[key] = [];
      }
      krollByKey[key].push(item);
    }

    const keys: { [key: string]: boolean } = {};
    const rows: InvoiceKrollCompareRow[] = [];

    for (let i = 0; i < invoiceItems.length; i++) {
      keys[this.getInvoiceKrollMatchKey(invoiceItems[i])] = true;
    }
    for (const key in krollByKey) {
      if (krollByKey.hasOwnProperty(key)) {
        keys[key] = true;
      }
    }

    for (const key in keys) {
      if (!keys.hasOwnProperty(key)) {
        continue;
      }
      const invoiceItem = invoiceItems.find(item => this.getInvoiceKrollMatchKey(item) === key);
      const krollGroup = krollByKey[key] || [];
      const krollLead = krollGroup.length ? krollGroup[0] : null;
      const source = invoiceItem || krollLead;
      if (!source) {
        continue;
      }

      const invoicePays = invoiceItem != null ? Number(invoiceItem.pays) : null;
      const krollPays = krollGroup.length ? this.resolveKrollPaysForGroup(krollGroup) : null;
      const paysMatch = invoicePays != null && krollPays != null
        && this.compareAmountsEqual(invoicePays, krollPays);

      const cost = invoiceItem != null ? Number(invoiceItem.cost) : Number(krollLead.cost);
      const fee = invoiceItem != null ? Number(invoiceItem.fee) : Number(krollLead.fee);
      const total = invoiceItem != null ? Number(invoiceItem.total) : Number(krollLead.total);
      const krollCost = krollLead != null ? Number(krollLead.cost) : null;
      const krollFee = krollLead != null ? Number(krollLead.fee) : null;
      const krollTotal = krollLead != null ? Number(krollLead.total) : null;
      const invoiceTaxable = invoiceItem != null ? Boolean(invoiceItem.taxable) : null;
      const krollTaxable = krollLead != null ? Boolean(krollLead.taxable) : null;
      const taxable = invoiceTaxable != null ? invoiceTaxable : (krollTaxable != null ? krollTaxable : false);
      const taxableDiffers = invoiceTaxable != null && krollTaxable != null && invoiceTaxable !== krollTaxable;

      rows.push({
        matchKey: key,
        fillDate: source.fillDate,
        origRxNum: source.origRxNum,
        rxNum: source.rxNum,
        quantity: source.quantity,
        drug: source.drug,
        din: source.din,
        cost: cost,
        fee: fee,
        total: total,
        subPlanCode: this.resolveKrollSubPlanCodes(krollGroup),
        taxable: taxable,
        invoiceTaxable: invoiceTaxable,
        krollTaxable: krollTaxable,
        taxableDiffers: taxableDiffers,
        reason: invoiceItem != null && invoiceItem.reason ? invoiceItem.reason : '',
        invoicePays: invoicePays,
        krollPays: krollPays,
        paysDiff: invoicePays != null && krollPays != null
          ? Number((invoicePays - krollPays).toFixed(2))
          : null,
        paysMatch: paysMatch,
        inInvoiceOnly: invoiceItem != null && krollGroup.length === 0,
        inKrollOnly: invoiceItem == null && krollGroup.length > 0,
        costDiffers: invoiceItem != null && krollLead != null && !this.compareAmountsEqual(cost, krollCost),
        feeDiffers: invoiceItem != null && krollLead != null && !this.compareAmountsEqual(fee, krollFee),
        totalDiffers: invoiceItem != null && krollLead != null && !this.compareAmountsEqual(total, krollTotal),
        krollCost: krollCost,
        krollFee: krollFee,
        krollTotal: krollTotal,
      });
    }

    rows.sort((a, b) => {
      const dateA = this.formatFillDateKey(a.fillDate);
      const dateB = this.formatFillDateKey(b.fillDate);
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      return Number(a.rxNum) - Number(b.rxNum);
    });

    this._invoiceKrollCompareRows = rows;
  }

  private getInvoiceKrollMatchKey(item: any): string {
    return String(item.rxNum) + '|' + this.formatFillDateKey(item.fillDate);
  }

  private formatFillDateKey(fillDate: any): string {
    if (!fillDate) {
      return '';
    }
    const formatted = this.datepipe.transform(fillDate, 'yyyy-MM-dd');
    return formatted ? formatted : String(fillDate);
  }

  private compareAmountsEqual(a: number, b: number): boolean {
    return Math.abs(Number(a) - Number(b)) < 0.005;
  }

  /** Sum patient Pays on Kroll lines for the same Rx (Cash plan lines when present). */
  private resolveKrollPaysForGroup(krollItems: any[]): number {
    if (!krollItems || !krollItems.length) {
      return 0;
    }
    const cashLines = krollItems.filter(item =>
      item.subPlanCode && String(item.subPlanCode).indexOf('Cash') >= 0);
    const lines = cashLines.length ? cashLines : krollItems;
    let sum = 0;
    for (let i = 0; i < lines.length; i++) {
      sum += Number(lines[i].pays || 0);
    }
    return Number(sum.toFixed(2));
  }

  private resolveKrollSubPlanCodes(krollItems: any[]): string {
    if (!krollItems || !krollItems.length) {
      return '';
    }
    const unique: string[] = [];
    for (let i = 0; i < krollItems.length; i++) {
      const code = krollItems[i].subPlanCode;
      if (code && unique.indexOf(code) < 0) {
        unique.push(code);
      }
    }
    return unique.join(' / ');
  }

  createSalesInvoice(): void {
    if (!this.invoice || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED) return;
    if (this.showLoadingMessage) return;

    this.ensureSageCustomerProfileThen(() => this.confirmSalesInvoiceFlow('create'));
  }

  updateSalesInvoice(): void {
    if (!this.invoice || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED) return;
    if (this.showLoadingMessage) return;
    if (!this.hasPersistedInvoiceId()) {
      Swal.fire({
        icon: 'warning',
        title: 'Invoice number required',
        text: 'Cannot update Sage invoice without an invoice number.',
      });
      return;
    }

    this.ensureSageCustomerProfileThen(() => this.confirmSalesInvoiceFlow('update'));
  }

  /** Loads Sage profile; runs callback only when Sage customer id is present. */
  private ensureSageCustomerProfileThen(onReady: () => void): void {
    this.invoiceService.getInvoiceSageProfile(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      res => {
        const profile = res && res.data;
        if (profile == null || profile.sageCustomerId == null || profile.sageCustomerId === undefined) {
          Swal.fire({
            icon: 'warning',
            title: 'Update Sage Profile Required',
            text: 'Please update Sage Profile.',
          });
          return;
        }
        onReady();
      },
      () => {
        Swal.fire({
          icon: 'warning',
          title: 'Update Sage Profile Required',
          text: 'Please update Sage Profile.',
        });
      }
    );
  }

  /** Create uses portal → Sage api/invoices/create; update uses api/invoices/update/{invoiceNumber} with same body shape (server-side). */
  private confirmSalesInvoiceFlow(mode: 'create' | 'update'): void {
    const inv = this.invoice;
    const startDateStr = this.invoice.startDate ? this.datepipe.transform(this.invoice.startDate, 'yyyy-MM-dd') : '';
    const isUpdate = mode === 'update';

    Swal.fire({
      icon: 'question',
      title: isUpdate ? 'Update Sales Invoice' : 'Create Sales Invoice',
      html: `
        <div class="text-start">
          <p class="mb-3">${isUpdate
            ? 'Do you want to update the sales invoice with the following details?'
            : 'Do you want to create a sales invoice with the following details?'}</p>
          <div class="mb-3">
            <strong>Resident Name:</strong> ${inv.billedTo || '-'}<br>
            <strong>Invoice ID:</strong> ${inv.id}<br>
            <strong>Invoice Number:</strong> ${inv.invoiceNumber || '-'}<br>
            <strong>Invoice Date:</strong> ${startDateStr}
          </div>
          <table class="table table-sm table-borderless mb-0">
            <tbody>
              <tr>
                <td>Taxable:</td>
                <td class="text-end">$${(inv.subTotalTaxableItems != null ? inv.subTotalTaxableItems : 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td>Non-Taxable:</td>
                <td class="text-end">$${(inv.subTotalNonTaxableItems != null ? inv.subTotalNonTaxableItems : 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td>HST:</td>
                <td class="text-end">$${(inv.hst != null ? inv.hst : 0).toFixed(2)}</td>
              </tr>
              <tr class="border-top">
                <td><strong>TOTAL:</strong></td>
                <td class="text-end"><strong>$${(inv.total != null ? inv.total : 0).toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: isUpdate ? 'Yes, update sales invoice' : 'Yes, create sales invoice',
    }).then(result => {
      if (!result.isConfirmed) return;

      this.showLoadingMessage = true;
      Swal.fire({
        title: isUpdate ? 'Updating sales invoice...' : 'Creating sales invoice...',
        text: 'This can take 10–20 seconds. Please wait.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        onOpen: () => { Swal.showLoading(); },
      });

      const request$ = isUpdate
        ? this.invoiceService.updateSalesInvoice(this.pharmacyId, this.facilityId, this.patientId, this.invoice.id)
        : this.invoiceService.createSalesInvoice(this.pharmacyId, this.facilityId, this.patientId, this.invoice.id);

      request$
        .pipe(
          timeout(60000),
          finalize(() => {
            Swal.close();
            this.showLoadingMessage = false;
          })
        )
        .subscribe(
          res => {
            if (!res.success) {
              this.loadErrorMessage(res.data);
              return;
            }
            this.sharedService.showSuccessMessage = true;
            setTimeout(() => { this.sharedService.showSuccessMessage = false; }, 5000);
            this.invoiceCacheService.setInvoices([]);
            this.loadFields();
          },
          err => {
            this.loadErrorMessage(err && err.data ? err.data : err);
          }
        );
    });
  }

  getDinColor(din: any): string {
    if (!din) return 'transparent';
    const dinStr = din.toString();

    const colors = [
      '#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF',
      '#E6B3FF', '#FFDAC1', '#E2F0CB', '#B5EAD7', '#C7CEEA',
      '#F497AD', '#D6AEFF', '#A2E1DB', '#FFD8B1', '#FFABAB',
      '#AFCBFF', '#FFCC99', '#D9A5B3', '#ACE5EE', '#CFDBC5'
    ];

    let hash = 0;
    for (let i = 0; i < dinStr.length; i++) {
      hash = dinStr.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }
}

// Highlight matching text in autocomplete options.
@Pipe({ name: 'highlight' })
export class HighlightPipe implements PipeTransform {
  transform(text: string, search): string {
    if (search && text && typeof search === 'string' && typeof text === 'string') {
      const pattern = search
        .replace(/[\-\[\]\/{}()*x+?.\\^$|]/g, '\\$&')
        .split(' ')
        .filter(t => t.length > 0)
        .join('|');
      const regex = new RegExp(pattern, 'gi');
      return search ? text.replace(regex, match => `<strong class="highlight">${match}</strong>`) : text;
    }
    return text;
  }
}
